import { useCallback, useEffect, useReducer, useRef } from 'react';

import { templatesV2EnhancedApi as templatesV2Api } from '@/services/api/templatesV2ServiceEnhanced';
import { DocumentTemplate } from '@/services/types/letters-types';
import { store } from '@/state/state';

// Dynamic import for docx-preview to avoid SSR issues
let renderAsync:
  | ((
      document: Blob,
      container: HTMLElement,
      stylesheetContainer?: HTMLElement,
      options?: Record<string, unknown>,
    ) => Promise<unknown>)
  | null = null;

interface PreviewState {
  previewBlob: Blob | null;
  previewHtml: string | null;
  isLoading: boolean;
  error: string | null;
  previewType: 'pdf' | 'html';
}

type UseTemplatePreviewResult = PreviewState;

interface UseTemplatePreviewOptions {
  /** Enable fast client-side HTML preview via v2 render (default: true) */
  useFastPreview?: boolean;
  /** Skip background PDF conversion, only show HTML preview (default: false) */
  skipPdfConversion?: boolean;
}

type PreviewAction =
  | { type: 'LOADING' }
  | {
      type: 'SUCCESS';
      payload: {
        previewBlob?: Blob | null;
        previewHtml?: string | null;
        previewType: 'pdf' | 'html';
      };
    }
  | { type: 'ERROR'; payload: string }
  | { type: 'RESET' };

const initialState: PreviewState = {
  previewBlob: null,
  previewHtml: null,
  isLoading: false,
  error: null,
  previewType: 'pdf',
};

function previewReducer(
  state: PreviewState,
  action: PreviewAction,
): PreviewState {
  switch (action.type) {
    case 'LOADING':
      return {
        ...state,
        isLoading: true,
        error: null,
        previewBlob: null,
        previewHtml: null,
      };
    case 'SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
        ...action.payload,
      };
    case 'ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        previewBlob: null,
        previewHtml: null,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

/**
 * Hook to preview a document template.
 *
 * Uses v2 endpoints exclusively:
 * - Fast preview: `POST /api/templates/{id}/render/` returns a rendered DOCX
 *   (fields resolved server-side), then `docx-preview` converts to HTML client-side.
 * - PDF preview: `POST /api/templates/{id}/render-to-pdf/` returns a PDF blob
 *   directly (runs in background after fast preview, or as primary if fast is disabled).
 *
 * Both endpoints accept optional `fields` and `matterId` — the server resolves
 * CONTEXT fields when a matterId is provided, and always strips SIGNING placeholders.
 */
export const useTemplatePreview = (
  template: DocumentTemplate | null,
  firmId: string,
  matterId?: string,
  fields?: Record<string, string | null>,
  options?: UseTemplatePreviewOptions,
): UseTemplatePreviewResult => {
  const [state, dispatch] = useReducer(previewReducer, initialState);
  const abortControllerRef = useRef<AbortController | null>(null);

  const shouldUseFastPreview = options?.useFastPreview !== false;
  const skipPdfConversion = options?.skipPdfConversion === true;

  // Render the template to a DOCX blob (fields resolved server-side),
  // then convert to HTML client-side via docx-preview.
  const generateFastHtmlPreview = useCallback(
    async (templateId: string, matterIdParam?: string) => {
      // 1. Server-side render: resolves fields, strips SIGNING placeholders
      const renderedDocx = await store
        .dispatch(
          templatesV2Api.endpoints.templateRender.initiate({
            id: templateId,
            fields: fields ?? undefined,
            matterId: matterIdParam ?? null,
          }),
        )
        .unwrap();

      // 2. Client-side conversion: DOCX → HTML via docx-preview
      if (!renderAsync) {
        const docxPreview = await import('docx-preview');
        renderAsync = docxPreview.renderAsync;
      }

      const tempContainer = document.createElement('div');
      await renderAsync(renderedDocx as Blob, tempContainer);
      return tempContainer.innerHTML;
    },
    [fields],
  );

  const generatePdfPreview = useCallback(
    async (templateId: string, matterIdParam?: string) => {
      const pdfBlob = await store
        .dispatch(
          templatesV2Api.endpoints.templateRenderToPdf.initiate({
            id: templateId,
            fields: fields ?? undefined,
            matterId: matterIdParam ?? null,
          }),
        )
        .unwrap();
      return pdfBlob;
    },
    [fields],
  );

  const fetchPreview = useCallback(
    async (templateParam: DocumentTemplate) => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      dispatch({ type: 'LOADING' });

      try {
        // --- Fast HTML preview path ---
        if (shouldUseFastPreview) {
          try {
            const htmlContent = await generateFastHtmlPreview(
              templateParam.id,
              matterId,
            );

            if (abortControllerRef.current?.signal.aborted) return;

            dispatch({
              type: 'SUCCESS',
              payload: { previewHtml: htmlContent, previewType: 'html' },
            });

            // Fetch PDF in background (unless skipped) so it's ready for
            // download / signature workflows
            if (!skipPdfConversion) {
              try {
                const pdfBlob = await generatePdfPreview(
                  templateParam.id,
                  matterId,
                );

                if (abortControllerRef.current?.signal.aborted) return;

                dispatch({
                  type: 'SUCCESS',
                  payload: {
                    previewHtml: htmlContent,
                    previewBlob: pdfBlob,
                    previewType: 'html',
                  },
                });
              } catch (pdfError) {
                // PDF conversion failed but HTML preview is already showing
                console.warn(
                  'Background PDF conversion failed, HTML preview still available:',
                  pdfError,
                );
              }
            }

            // Fast preview succeeded — don't fall through to PDF-only path
            return;
          } catch (fastPreviewError) {
            console.warn(
              'Fast preview failed, falling back to PDF:',
              fastPreviewError,
            );
            // Fall through to PDF-only path
          }
        }

        // --- PDF-only path (fallback or when fast preview is disabled) ---
        const pdfBlob = await generatePdfPreview(templateParam.id, matterId);

        if (abortControllerRef.current?.signal.aborted) return;

        dispatch({
          type: 'SUCCESS',
          payload: { previewBlob: pdfBlob, previewType: 'pdf' },
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error('Error generating template preview:', err);
        dispatch({
          type: 'ERROR',
          payload:
            err instanceof Error ? err.message : 'Failed to generate preview',
        });
      }
    },
    [
      shouldUseFastPreview,
      skipPdfConversion,
      generateFastHtmlPreview,
      generatePdfPreview,
      matterId,
    ],
  );

  useEffect(() => {
    if (!template || !firmId) {
      dispatch({ type: 'RESET' });
      return undefined;
    }

    // EXTERNAL templates (e.g. Adobe Sign) have no downloadable file — skip preview
    if (template.format === 'EXTERNAL') {
      dispatch({ type: 'RESET' });
      return undefined;
    }

    fetchPreview(template);

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [template, firmId, matterId, fetchPreview]);

  return state;
};
